import { toUserSearchRequestDto } from '../model/mappers/search-request.dto';
import { toUserModel } from '../model/mappers/user.model';

import type { UsersSearchRequestDto } from '../model/types/search-request.dto';
import type { UsersSearchRequestModel } from '../model/types/search-request.model';
import type { UserModel } from '../model/types/users.model';
import type { User } from '@/app/types/User';
import type { SearchResponseDto } from '@/features/Shared/model/types/search-response.dto';
import type { SearchResponseModel } from '@/features/Shared/model/types/search-response.model';

import { apiSlice } from '@/app/api/api';

export const usersApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		users: builder.infiniteQuery<
			SearchResponseModel<UserModel>,
			UsersSearchRequestModel,
			string | undefined
		>({
			infiniteQueryOptions: {
				initialPageParam: undefined,
				getNextPageParam: (lastPage: SearchResponseModel<UserModel>) => {
					return lastPage.moreResults ? (lastPage.continuation ?? undefined) : undefined;
				},
			},

			query: ({ queryArg, pageParam }) => ({
				url: '/users/search',
				method: 'POST',
				body: {
					...toUserSearchRequestDto(queryArg),
					continuation_token: pageParam ?? undefined,
				} satisfies UsersSearchRequestDto,
			}),
			transformResponse: (response: SearchResponseDto<User>) => {
				return {
					items: (response.items ?? []).map(toUserModel),
					continuation: response.meta.continuation,
					moreResults: response.meta.more_results,
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled;
					if (data?.pages) {
						data.pages.forEach((page) => {
							page.items.forEach((user) => {
								dispatch(
									usersApiSlice.util.updateQueryData(
										'useSearchResolvedUsers',
										undefined,
										(draft) => {
											draft[user.id] = user;
										}
									)
								);
							});
						});
					}
				} catch {
					// Handle error if needed
				}
			},
		}),
		useSearchResolvedUsers: builder.query<Record<string, UserModel>, void>({
			queryFn: () => ({ data: {} }),
		}),
	}),

	overrideExisting: false,
});

export const { useUsersInfiniteQuery, useUseSearchResolvedUsersQuery } = usersApiSlice;
